import { useQuery } from '@tanstack/react-query';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { testsApi } from '@/api/tests';
import TestApplicationInstructions from '@/components/tests/TestApplicationInstructions';
import TestPageLayout from '@/components/tests/TestPageLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import useScrollProgress from '@/hooks/use-scroll-progress';
import { PatientDetailPage } from '@/pages/constants';
import { toTitleCase } from '@/utils/toTitleCase';
import TestResponseViewer from './components/TestResponseViewer';

export default function TestResponseView() {
    const { patientId, assignmentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const scrollProgress = useScrollProgress();

    const {
        data: testAnswers,
        isLoading: isLoadingAnswers,
        error: answersError,
    } = useQuery({
        queryKey: ['patient-test-answers', patientId, assignmentId],
        queryFn: () => testsApi.getAssignmentAnswers(patientId || '', assignmentId || ''),
        enabled: Boolean(patientId && assignmentId),
        retry: false,
    });

    const { data: testDefinition, isLoading: isLoadingTestDefinition } = useQuery({
        queryKey: ['tests', testAnswers?.test_definition_id],
        queryFn: () => testsApi.getById(testAnswers?.test_definition_id || ''),
        enabled: Boolean(testAnswers?.test_definition_id),
        retry: false,
    });

    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get('returnTo');
    const fallbackReturn = `/${generatePath(PatientDetailPage, {
        patientId: patientId || '',
    })}`;

    const handleBack = () => {
        const target = returnTo && returnTo.startsWith('/') ? returnTo : fallbackReturn;
        navigate(target);
    };

    if (isLoadingAnswers || (Boolean(testAnswers?.test_definition_id) && isLoadingTestDefinition)) {
        return <Loading />;
    }

    if (answersError) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Respostas não encontradas
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        Não foi possível recuperar as respostas deste instrumento.
                    </p>
                    <Button onClick={handleBack}>Voltar para o paciente</Button>
                </div>
            </div>
        );
    }

    const answers = testAnswers?.answers || {};
    const hasDefinition = Boolean(testDefinition);
    const hasSections = Boolean(testDefinition?.sections?.length);

    if (hasDefinition && !hasSections) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Estrutura do instrumento indisponível
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        Este instrumento ainda não possui perguntas configuradas.
                    </p>
                    <Button onClick={handleBack}>Voltar para o paciente</Button>
                </div>
            </div>
        );
    }

    return (
        <TestPageLayout progress={scrollProgress} onBack={handleBack}>
            <div className="rounded-xl bg-card p-6 shadow-elevation-1 md:p-8">
                <div className="mb-6 border-b pb-4">
                    <h1 className="mb-1 text-2xl font-bold text-foreground">
                        {testDefinition?.name || testAnswers?.test_name || 'Instrumento'}
                    </h1>
                </div>

                <TestApplicationInstructions
                    instructions={testDefinition?.details?.application_instructions}
                    className="mb-6"
                />

                <Alert variant="info" className="mb-6">
                    <strong>Visualização de Respostas:</strong> Abaixo estão as respostas enviadas
                    pelo paciente.
                </Alert>

                {hasDefinition && testDefinition ? (
                    <TestResponseViewer test={testDefinition} answers={answers} />
                ) : (
                    <div className="space-y-6">
                        {Object.entries(answers).map(([key, value]) => (
                            <div key={key} className="border-b border-border pb-6 last:border-0">
                                <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                                    {toTitleCase(key.replace(/_/g, ' '))}
                                </h3>
                                <div className="text-lg font-semibold leading-relaxed text-foreground">
                                    {typeof value === 'string' ? value : String(value ?? '-')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </TestPageLayout>
    );
}
